import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { FaMinus, FaPlus } from 'react-icons/fa';

const renderWidth = (value) => {
  const v = +value;
  if (v < 10) return 'w-3';
  if (v < 100) return 'w-5';
  return 'w-6';
};

function ButtonCounter({
  max,
  name,
  value,
  onChange,
  disabled,
  onSetFieldValue,
  enableNegative,
  formatValue,
  autoWidth,
  buttonClassName,
  iconClassName,
  numberClassName,
  maxDescription,
  disabledDescription,
}) {
  const handleOnChange = (type) => (e) => {
    e.preventDefault();
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, type ? value + 1 : value - 1);
      return;
    }
    if (typeof onChange === 'function')
      onChange((prev) => ({
        ...prev,
        [name]: type ? value + 1 : value - 1,
      }));
  };

  return (
    <div className="flex justify-center items-center has-tooltip">
      {disabledDescription && disabled ? (
        <span className="tooltip absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded shadow-lg p-2 bg-gray-900 bg-opacity-80 text-white text-xs w-32 z-10">
          {disabledDescription}
        </span>
      ) : null}
      <button
        type="button"
        disabled={disabled || (value === 1 && !enableNegative)}
        onClick={handleOnChange(0)}
        className={cn(
          'outline-none rounded border border-gray-200 p-1',
          buttonClassName
        )}
      >
        <FaMinus
          className={cn(
            'w-3 h-3',
            {
              'text-gray-400': value === 1 && !enableNegative,
              'text-red-600': !disabled && value !== 1,
            },
            {
              'text-gray-400': disabled,
            },
            iconClassName
          )}
        />
      </button>
      <span
        className={cn(
          'font-medium text-sm text-center mx-1',
          !autoWidth ? renderWidth(value) : '',
          { 'px-0.5': autoWidth },
          numberClassName
        )}
      >
        {formatValue ? formatValue(value) : value}
      </span>
      <button
        type="button"
        disabled={disabled ? true : value === max}
        onClick={handleOnChange(1)}
        className={cn(
          'outline-none rounded border border-gray-200 p-1 has-tooltip',
          buttonClassName
        )}
      >
        {value === max ? (
          <span className="tooltip absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded shadow-lg p-2 bg-gray-900 bg-opacity-80 text-white text-xs w-24 z-10">
            {maxDescription || `Maximum length is ${max}`}
          </span>
        ) : null}

        <FaPlus
          className={cn(
            'w-3 h-3',
            {
              'text-gray-400': value === max,
              'text-green-600': !disabled && value !== max,
            },
            {
              'text-gray-400': disabled,
            },
            iconClassName
          )}
        />
      </button>
    </div>
  );
}
ButtonCounter.defaultProps = {
  value: 1,
  onSetFieldValue: false,
  onChange: false,
  max: 99999999,
  disabled: false,
  enableNegative: false,
  formatValue: false,
  autoWidth: false,
  buttonClassName: '',
  iconClassName: '',
  numberClassName: '',
  maxDescription: '',
  disabledDescription: '',
};

ButtonCounter.propTypes = {
  max: PropTypes.number,
  enableNegative: PropTypes.bool,
  buttonClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  numberClassName: PropTypes.string,
  disabledDescription: PropTypes.string,
  name: PropTypes.string.isRequired,
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
  formatValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  disabled: PropTypes.bool,
  autoWidth: PropTypes.bool,
  maxDescription: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(Object)]),
};

export default ButtonCounter;
